import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Copy, Check, Pencil, X, Save, RotateCcw, MoreHorizontal, ChevronDown, ChevronUp, CopyPlus, Shield, TestTube, Archive, Library, Sparkles, Loader2 } from "lucide-react";
import API from "@/lib/api";
import { toast } from "sonner";

const QUALITY_COLORS = {
  template: "border-l-[var(--pf-border)]",
  adapted: "border-l-[var(--pf-accent)]",
  sharpened: "border-l-[#16A34A]",
  edited: "border-l-[#EAB308]",
  playbook: "border-l-[#9333EA]",
  contextualized: "border-l-[#0D9488]",
};

const STAGE_CONFIG = {
  draft: { label: "Draft", color: "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)]" },
  tested: { label: "Tested", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  production: { label: "Production", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  deprecated: { label: "Deprecated", color: "bg-[var(--pf-surface)] text-[var(--pf-text-muted)] line-through" },
};

export function PromptBlock({ promptId, category, index, title, content, tags, stage, quality, selected, onSelect, onUpdate, onRegenerated, onStageChange, onDuplicated }) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(content || "");
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [contextualizing, setContextualizing] = useState(false);
  const textareaRef = useRef(null);

  const blockStage = stage || "draft";
  const blockQuality = quality || "template";
  const isProduction = blockStage === "production";
  const isDeprecated = blockStage === "deprecated";
  const qualityBorder = QUALITY_COLORS[blockQuality] || QUALITY_COLORS.template;
  const stageConf = STAGE_CONFIG[blockStage] || STAGE_CONFIG.draft;
  const hasVariables = (content || "").includes("{") && (content || "").includes("}");

  useEffect(() => { setEditValue(content || ""); }, [content]);
  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [editing]);

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
  };

  const handleCopy = async () => { await copyText(content); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const handleSave = async () => {
    if (!promptId) return;
    setSaving(true);
    try {
      await API.put(`/prompts/${promptId}/field`, { category, block_index: index, content: editValue });
      setEditing(false);
      if (onUpdate) onUpdate(editValue);
    } catch (e) {
      if (e.response?.status === 403) alert("Production blocks cannot be edited. Duplicate as draft first.");
      console.error("Save block error:", e);
    } finally { setSaving(false); }
  };

  const handleRegenerate = async () => {
    if (!promptId || isProduction) return;
    setRegenerating(true);
    try {
      const { data } = await API.post(`/prompts/${promptId}/regenerate-block`, { category, block_index: index });
      if (onRegenerated) onRegenerated(data.block);
    } catch (e) { console.error(e); } finally { setRegenerating(false); }
  };

  const handleStageChange = async (newStage) => {
    if (!promptId) return;
    try {
      await API.put(`/prompts/${promptId}/block-stage`, { category, block_index: index, stage: newStage });
      if (onStageChange) onStageChange(newStage);
    } catch (e) { console.error(e); }
  };

  const handleDuplicate = async () => {
    if (!promptId) return;
    try {
      const { data } = await API.post(`/prompts/${promptId}/duplicate-block`, { category, block_index: index });
      if (onDuplicated) onDuplicated(data.block);
    } catch (e) { console.error(e); }
  };

  const handleContextualize = async () => {
    setContextualizing(true);
    try {
      const { data } = await API.post(`/prompts/${promptId}/contextualize-block`, { category, block_index: index });
      if (data.block && onRegenerated) onRegenerated(data.block);
      toast.success("Block contextualized");
    } catch (e) {
      toast.error("Contextualization failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setContextualizing(false);
    }
  };

  if (isDeprecated && !expanded) {
    return (
      <div className={`border border-[var(--pf-border)] border-l-4 ${qualityBorder} bg-[var(--pf-bg)] opacity-50 px-4 py-2 flex items-center justify-between`} data-testid={`block-${category}-${index}`}>
        <span className="text-xs text-[var(--pf-text-muted)] line-through">{title}</span>
        <button onClick={() => setExpanded(true)} className="text-[10px] text-[var(--pf-accent)]">Show</button>
      </div>
    );
  }

  return (
    <div
      className={`border border-[var(--pf-border)] border-l-4 ${qualityBorder} bg-[var(--pf-bg)] group transition-colors duration-150 ${isDeprecated ? "opacity-50" : "hover:border-[var(--pf-accent)]"}`}
      data-testid={`block-${category}-${index}`}
    >
      {/* Header: checkbox + title + stage + actions */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--pf-border)]">
        {onSelect && (
          <input
            type="checkbox"
            checked={selected || false}
            onChange={() => onSelect(index)}
            data-testid={`block-check-${category}-${index}`}
            className="w-3.5 h-3.5 rounded-none border-[var(--pf-border)] accent-[var(--pf-accent)] cursor-pointer shrink-0"
          />
        )}
        <span className="text-xs font-semibold text-[var(--pf-text)] uppercase tracking-[0.1em] flex-1 truncate">{title}</span>
        <span className={`text-[10px] px-1.5 py-0.5 font-medium shrink-0 ${stageConf.color}`}>{stageConf.label}</span>
        {blockQuality === "playbook" && hasVariables && (
          <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 font-mono font-medium shrink-0" data-testid={`block-vars-${category}-${index}`}>{"{x}"}</span>
        )}
        {blockQuality === "contextualized" && (
          <span className="text-[10px] px-1.5 py-0.5 bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 font-medium shrink-0" data-testid={`block-ctx-${category}-${index}`}>contextualized</span>
        )}

        <div className="flex items-center gap-0.5 shrink-0">
          <TooltipProvider delayDuration={300}>
            {editing ? (
              <>
                <Button variant="ghost" size="sm" onClick={handleSave} disabled={saving} data-testid={`block-save-${category}-${index}`} className="h-7 px-2 rounded-none text-xs text-[var(--pf-accent)] hover:bg-[var(--pf-surface)]"><Save className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={() => { setEditValue(content || ""); setEditing(false); }} className="h-7 px-2 rounded-none text-xs hover:bg-[var(--pf-surface)]"><X className="w-3 h-3" /></Button>
              </>
            ) : (
              <>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={handleCopy} data-testid={`block-copy-${category}-${index}`} className="h-7 px-2 rounded-none text-xs hover:bg-[var(--pf-surface)] opacity-0 group-hover:opacity-100 transition-opacity">
                    {copied ? <Check className="w-3 h-3 text-[#16A34A]" /> : <Copy className="w-3 h-3" />}
                  </Button>
                </TooltipTrigger><TooltipContent side="bottom" className="text-xs rounded-none">Copy</TooltipContent></Tooltip>

                {!isProduction && !isDeprecated && (
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid={`block-edit-${category}-${index}`} className="h-7 px-2 rounded-none text-xs hover:bg-[var(--pf-surface)] opacity-0 group-hover:opacity-100 transition-opacity"><Pencil className="w-3 h-3" /></Button>
                  </TooltipTrigger><TooltipContent side="bottom" className="text-xs rounded-none">Edit</TooltipContent></Tooltip>
                )}

                {hasVariables && !editing && (
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={handleContextualize} disabled={contextualizing} data-testid={`block-contextualize-${category}-${index}`}
                      className="h-7 px-2 rounded-none text-xs hover:bg-[var(--pf-surface)] opacity-0 group-hover:opacity-100 transition-opacity text-[var(--pf-accent)]">
                      {contextualizing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    </Button>
                  </TooltipTrigger><TooltipContent side="bottom" className="text-xs rounded-none">Contextualize</TooltipContent></Tooltip>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 px-2 rounded-none text-xs hover:bg-[var(--pf-surface)] opacity-0 group-hover:opacity-100 transition-opacity"><MoreHorizontal className="w-3 h-3" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none border-[var(--pf-border)] w-44">
                    {!isProduction && !isDeprecated && (
                      <DropdownMenuItem onClick={handleRegenerate} disabled={regenerating} className="rounded-none text-xs cursor-pointer"><RotateCcw className="w-3 h-3 mr-2" />Regenerate</DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleDuplicate} className="rounded-none text-xs cursor-pointer"><CopyPlus className="w-3 h-3 mr-2" />Duplicate as Draft</DropdownMenuItem>
                    <DropdownMenuItem onClick={async () => {
                      try {
                        await API.post("/library/promote", { prompt_id: promptId, category, block_index: index });
                        toast.success("Promoted to library");
                      } catch { toast.error("Promote failed"); }
                    }} className="rounded-none text-xs cursor-pointer"><Library className="w-3 h-3 mr-2" />Promote to Library</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {blockStage === "draft" && <DropdownMenuItem onClick={() => handleStageChange("tested")} className="rounded-none text-xs cursor-pointer"><TestTube className="w-3 h-3 mr-2" />Mark as Tested</DropdownMenuItem>}
                    {blockStage === "tested" && <DropdownMenuItem onClick={() => handleStageChange("production")} className="rounded-none text-xs cursor-pointer"><Shield className="w-3 h-3 mr-2" />Promote to Production</DropdownMenuItem>}
                    {blockStage === "production" && <DropdownMenuItem onClick={() => handleStageChange("tested")} className="rounded-none text-xs cursor-pointer"><TestTube className="w-3 h-3 mr-2" />Demote to Tested</DropdownMenuItem>}
                    {blockStage !== "deprecated" && <DropdownMenuItem onClick={() => handleStageChange("deprecated")} className="rounded-none text-xs cursor-pointer text-red-500"><Archive className="w-3 h-3 mr-2" />Deprecate</DropdownMenuItem>}
                    {blockStage === "deprecated" && <DropdownMenuItem onClick={() => handleStageChange("draft")} className="rounded-none text-xs cursor-pointer"><RotateCcw className="w-3 h-3 mr-2" />Restore as Draft</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </TooltipProvider>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3">
        {regenerating ? (
          <div className="space-y-2"><div className="h-3 w-full bg-[var(--pf-surface)] skeleton-pulse" /><div className="h-3 w-4/5 bg-[var(--pf-surface)] skeleton-pulse" /><div className="h-3 w-3/5 bg-[var(--pf-surface)] skeleton-pulse" /></div>
        ) : editing ? (
          <textarea ref={textareaRef} value={editValue}
            onChange={(e) => { setEditValue(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSave(); }}
            className="w-full font-mono text-sm leading-relaxed text-[var(--pf-text)] bg-[var(--pf-surface)] border border-[var(--pf-border)] p-3 resize-none focus:outline-none focus:ring-2 focus:ring-[var(--pf-accent)]"
            style={{ minHeight: "60px" }} />
        ) : (
          <>
            <p className="font-mono text-sm leading-relaxed text-[var(--pf-text)] whitespace-pre-wrap">{content}</p>
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2.5">
                {tags.map((tag) => (
                  <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-surface)] text-[var(--pf-text-muted)] border border-[var(--pf-border)]">{tag}</span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
